import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import {
  Box, Button, Typography, Breadcrumbs, TextField,
  Autocomplete, InputBase, useTheme,
} from "@mui/material";

import ReceiptIcon          from "@mui/icons-material/Receipt";
import ChevronRightIcon     from "@mui/icons-material/ChevronRight";
import PersonAddIcon        from "@mui/icons-material/PersonAdd";
import SaveIcon             from "@mui/icons-material/Save";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import CancelIcon           from "@mui/icons-material/Cancel";
import SearchIcon           from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PersonIcon           from "@mui/icons-material/Person";
import EditNoteIcon         from "@mui/icons-material/EditNote";

import { Formik, FieldArray } from "formik";
import * as yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

import { requester }      from "api/pesabackend";
import InvoiceDetailForm  from "./invoiceDetailForm";
import InvoiceConfirmView from "../views/invoiceConfirmView";
import FixedSizeDialog    from "components/fixedSizeDialog";
import CustomerForm       from "scenes/customer/forms/customerForm";
import TopSnack           from "components/TopSnack";

/* ─────────────────────────────────────────
   Schemas
───────────────────────────────────────── */
const invoiceSchema = yup.object().shape({
  invoice_products: yup.array()
    .of(yup.object().shape({
      related_product:      yup.number().required("required"),
      related_product_unit: yup.number().required("required"),
      batch:                yup.string().required("required"),
      quantity:             yup.number().min(0.5, "Min 0.5").required("required"),
      discount:             yup.number().required("required"),
    }))
    .required("Add at least one item")
    .min(1, "At least one item is required"),
});

const invoiceCustomerSchema = yup.object().shape({
  related_customer: yup.number().required("required"),
});


const normaliseProduct = (p) => ({
  related_product:      p.related_product,
  related_product_unit: p.related_product_unit,
  batch:                p.batch,
  quantity:             p.quantity,
  sale_price:           p.sale_price,
  discount:             p.discount,
  total:                p.total,
  product_name:         p.product_info?.product_name ?? p.product_name ?? "",
  product_unit:         p.product_unit ?? "",
  data_id:              p.id ?? uuidv4(),
});


const ModePill = memo(({ label, icon, active, onClick, accent, border, textSec }) => (
  <Box onClick={onClick} sx={{
    display: "flex", alignItems: "center", gap: "6px",
    px: "0.875rem", py: "0.4rem", borderRadius: "20px", cursor: "pointer",
    border: `1px solid ${active ? accent : border}`,
    backgroundColor: active ? "rgba(29,158,117,0.1)" : "transparent",
    color: active ? accent : textSec,
    fontSize: "13px", fontWeight: 500, transition: "all 0.15s",
    "&:hover": { borderColor: accent, color: accent },
    userSelect: "none",
  }}>
    {icon}{label}
  </Box>
));

/* ─────────────────────────────────────────
   Main component
───────────────────────────────────────── */
const InvoiceEditForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_id        = useSelector((s) => s.authReducer.business_id);
  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);

  const [editMode, setEditMode] = useState("customer");

  /* ── customer ── */
  const [customer,         setCustomer]         = useState(location.state.related_customer ?? undefined);
  const [customers,        setCustomers]        = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  /* ── new customer ── */
  const [openNewCustomer,      setOpenNewCustomer]      = useState(false);
  const [firstName,            setfirstName]            = useState("");
  const [lastName,             setLastName]             = useState(".");
  const [email,                setEmail]                = useState("a@gmail.com");
  const [address,              setAddress]              = useState("");
  const [phoneNumber,          setPhoneNumber]          = useState("");
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);

  /* ── items ── */
  const [billDate,            setBillDate]            = useState(moment(location.state.date_created).format("YYYY-MM-DDTHH:mm"));
  const [open,                setOpen]                = useState(false);
  const [isSubmittingInvoice, setIsSubmittingInvoice] = useState(false);
  const [searchQuery,         setSearchQuery]         = useState("");
  const [searchResults,       setSearchResults]       = useState([]);
  const [showResults,         setShowResults]         = useState(false);
  const [openSnack,           setOpenSnack]           = useState({ open: false, vertical: "top", horizontal: "center", message: "", severity: "success" });

  /* ── Normalise products once on mount ── */
  const normalisedProducts = useMemo(
    () => location.state.products.map(normaliseProduct),
    [] // eslint-disable-line
  );

  const initialValues = useMemo(() => ({
    invoice_description: location.state.description ?? "",
    total_amount:        location.state.total_amount,
    total_discount:      0,
    invoice_products:    normalisedProducts,
  }), []); // eslint-disable-line

  const [invoiceDetailFormData, setInvoiceDetailFormData] = useState(normalisedProducts);
  const [totalDiscount, setTotalDiscount] = useState(
    normalisedProducts.reduce((t, p) => t + parseFloat(p.discount || 0), 0)
  );
  const [grandTotal, setGrandTotal] = useState(
    normalisedProducts.reduce((t, p) => t + parseFloat(p.total || 0), 0)
  );

  const inputSx = useMemo(() => ({
    "& .MuiInputBase-root": { fontSize: "13px", height: "34px", backgroundColor: cardBg, borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  }), [cardBg, border]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await requester.get(`/customers/list?search=False&business_branch=${business_branch_id}`);
      setCustomers(res.data.data);
    } catch {}
  }, [business_branch_id]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  /* ── Totals ── */
  const recalcTotals = useCallback((arr) => {
    setGrandTotal(arr.reduce((t, i)    => t + Number(i.total),    0));
    setTotalDiscount(arr.reduce((t, i) => t + Number(i.discount), 0));
  }, []);

  /* ── Item handlers — same signatures InvoiceDetailForm calls ── */
  const handleItemAmountChange = useCallback((
    price, total, quantity, batch,
    product_name, product_unit,
    related_product, related_product_unit, index,
  ) => {
    setInvoiceDetailFormData((prev) => {
      const next = prev.map((item, idx) =>
        idx === index
          ? { ...item, sale_price: parseFloat(price), total: parseFloat(total), quantity: parseFloat(quantity), batch, product_unit, product_name, related_product, related_product_unit }
          : item
      );
      recalcTotals(next);
      return next;
    });
  }, [recalcTotals]);

  const handleItemDiscountChange = useCallback((e, index) => {
    setInvoiceDetailFormData((prev) => {
      const next = prev.map((item, idx) =>
        idx === index ? { ...item, discount: e.target.value } : item
      );
      recalcTotals(next);
      return next;
    });
  }, [recalcTotals]);

  const handleRemoveProductDetailRow = useCallback((index) => {
    setInvoiceDetailFormData((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      recalcTotals(next);
      return next;
    });
  }, [recalcTotals]);

  /* ── Search ── */
  const handleSearchChange = useCallback(async (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (q.length >= 2) {
      try {
        const res = await requester.get(
          `/inventory/products/list?business_branch=${business_branch_id}&search=${q}&typeFilter=all`
        );
        setSearchResults(res.data.data ?? []);
        setShowResults(true);
      } catch { setSearchResults([]); }
    } else {
      setShowResults(false);
      setSearchResults([]);
    }
  }, [business_branch_id]);

  /* ── Customer handlers ── */
  const handleChangeCustomer = useCallback((value) => {
    if (value) { setCustomer(value.id); setValidationErrors((p) => ({ ...p, related_customer: "" })); }
    else        { setCustomer(undefined); setValidationErrors((p) => ({ ...p, related_customer: "This is required" })); }
  }, []);

  const editCustomer = useCallback(async () => {
    if (isEditingCustomer) return;
    try { await invoiceCustomerSchema.validate({ related_customer: customer }, { abortEarly: false }); }
    catch (err) {
      const errs = {};
      err.inner.forEach((e) => { errs[e.path] = e.message; });
      setValidationErrors(errs);
      return;
    }
    setIsEditingCustomer(true);
    try {
      await requester.post("/billing/invoices/customer/edit", { invoice_id: location.state.id, related_customer: customer });
      setOpenSnack({ open: true, vertical: "top", horizontal: "center", message: "Customer updated successfully", severity: "success" });
    } catch {}
    setIsEditingCustomer(false);
  }, [customer, location.state.id, isEditingCustomer]);

  const saveNewCustomer = useCallback(async () => {
    setIsSubmittingCustomer(true);
    try {
      await requester.post("/customers/add", {
        related_business: business_id, related_business_branch: business_branch_id,
        first_name: firstName, last_name: lastName, email, address, phone_number: phoneNumber,
      });
      setOpenNewCustomer(false);
      fetchCustomers();
    } catch {}
    setIsSubmittingCustomer(false);
  }, [business_id, business_branch_id, firstName, lastName, email, address, phoneNumber, fetchCustomers]);

  const saveInvoice = useCallback(async () => {
    setIsSubmittingInvoice(true);
    try {
      await requester.post("/billing/invoices/edit", {
        invoice_id:       location.state.id,
        total_amount:     grandTotal,
        invoice_products: invoiceDetailFormData,
      });
      navigate("/billing");
    } catch {
      setOpenSnack({ open: true, vertical: "top", horizontal: "center", message: "Please check the bill details", severity: "error" });
    }
    setIsSubmittingInvoice(false);
  }, [location.state.id, grandTotal, invoiceDetailFormData, navigate]);

  const handleCloseSnack = useCallback((_, reason) => {
    if (reason !== "clickaway") setOpenSnack((p) => ({ ...p, open: false }));
  }, []);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", backgroundColor: bodyBg, overflow: "hidden" }}>

      {/* ══ FIXED HEADER ══ */}
      <Box sx={{ flexShrink: 0, px: "1.5rem", pt: "1.25rem", pb: "1rem" }}>

        <Breadcrumbs separator={<ChevronRightIcon sx={{ fontSize: "14px", color: border }} />} sx={{ mb: "0.75rem" }}>
          <Typography
            onClick={() => navigate("/billing")}
            sx={{ fontSize: "12px", color: accent, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { textDecoration: "underline" } }}
          >
            <ReceiptIcon sx={{ fontSize: "14px" }} /> Sales
          </Typography>
          <Typography sx={{ fontSize: "12px", color: textSec }}>Edit Sales Bill</Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "1rem" }}>
          <Box sx={{ width: "36px", height: "36px", borderRadius: "9px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <ReceiptIcon sx={{ fontSize: "19px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "20px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Edit Sales Bill
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "1px" }}>
              Invoice #{location.state.invoice_number ?? "—"}
            </Typography>
          </Box>
        </Box>

        {/* Meta bar */}
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", px: "14px", py: "10px", backgroundColor: cardBg, border: `0.5px solid ${border}`, borderRadius: "10px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
              Bill Date
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", px: "10px", py: "5px", border: `0.5px solid ${border}`, borderRadius: "8px", fontSize: "13px", color: textPri, backgroundColor: bodyBg }}>
              <input
                type="datetime-local"
                value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
                style={{ border: "none", outline: "none", background: "transparent", fontSize: "13px", color: "inherit", fontFamily: "inherit" }}
              />
            </Box>
          </Box>

          <Box sx={{ width: "1px", height: "28px", backgroundColor: border }} />

          <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: textSec, mr: "2px" }}>
              Editing
            </Typography>
            <ModePill
              label="Customer" icon={<PersonIcon sx={{ fontSize: "14px", mr: "4px" }} />}
              active={editMode === "customer"} onClick={() => setEditMode("customer")}
              accent={accent} border={border} textSec={textSec}
            />
            <ModePill
              label="Items" icon={<EditNoteIcon sx={{ fontSize: "14px", mr: "4px" }} />}
              active={editMode === "items"} onClick={() => setEditMode("items")}
              accent={accent} border={border} textSec={textSec}
            />
          </Box>
        </Box>
      </Box>

      {/* ══ CUSTOMER PANEL ══ */}
      {editMode === "customer" && (
        <Box sx={{ flex: 1, overflowY: "auto", px: "1.5rem", py: "0.5rem" }}>
          <Box sx={{ backgroundColor: cardBg, border: `0.5px solid ${border}`, borderRadius: "12px", overflow: "hidden" }}>

            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "1.25rem", py: "1rem", borderBottom: `0.5px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.04)" }}>
              <Box sx={{ width: "28px", height: "28px", borderRadius: "7px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
                <PersonIcon sx={{ fontSize: "15px" }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>Customer Assignment</Typography>
                <Typography sx={{ fontSize: "11px", color: textSec, mt: "1px" }}>Change or attach a customer to this bill</Typography>
              </Box>
            </Box>

            <Box sx={{ p: "1.25rem 1.5rem", display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end" }}>
              <Box sx={{ flex: "1 1 280px" }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec, mb: "5px" }}>
                  Select customer
                </Typography>
                <Autocomplete
                  onChange={(_, value) => handleChangeCustomer(value)}
                  options={customers}
                  defaultValue={location.state.customer_info}
                  getOptionLabel={(o) => `${o.related_user.first_name} ${o.related_user.last_name}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderOption={(props, o) => (
                    <Box component="li" {...props} key={o.id}>
                      {o.related_user.first_name} {o.related_user.last_name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params} size="small" placeholder="Search customer…"
                      error={Boolean(validationErrors.related_customer)}
                      helperText={validationErrors.related_customer}
                      sx={inputSx}
                    />
                  )}
                  sx={{ width: "100%" }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <Button
                  onClick={editCustomer}
                  disabled={isEditingCustomer}
                  startIcon={<SaveIcon sx={{ fontSize: "14px" }} />}
                  sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", textTransform: "none", backgroundColor: accent, color: "#fff", "&:hover": { backgroundColor: "#0F6E56" } }}
                >
                  Save Customer
                </Button>
                <Button
                  onClick={() => setOpenNewCustomer(true)}
                  startIcon={<PersonAddIcon sx={{ fontSize: "14px" }} />}
                  sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", textTransform: "none", border: `1px solid ${border}`, color: accent, backgroundColor: "transparent", "&:hover": { backgroundColor: "rgba(29,158,117,0.07)" } }}
                >
                  New Customer
                </Button>
              </Box>
            </Box>

            {/* Current customer badge */}
            {location.state.customer_info && (
              <Box sx={{ mx: "1.5rem", mb: "1.25rem", p: "0.875rem 1rem", backgroundColor: "rgba(29,158,117,0.05)", border: `1px solid rgba(29,158,117,0.14)`, borderRadius: "8px", display: "flex", alignItems: "center", gap: "10px" }}>
                <Box sx={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0, fontSize: "12px", fontWeight: 600 }}>
                  {location.state.customer_info?.related_user?.first_name?.[0]}
                  {location.state.customer_info?.related_user?.last_name?.[0]}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                    {location.state.customer_info.related_user.first_name} {location.state.customer_info.related_user.last_name}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: textSec }}>Currently assigned customer</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ══ ITEMS PANEL ══ */}
      {editMode === "items" && (
        <Formik
          initialValues={initialValues}
          validationSchema={invoiceSchema}
          validateOnMount validateOnChange={false} validateOnBlur={false}
        >
          {({ values, errors, touched, handleBlur, handleChange, validateForm, isValid, resetForm, setFieldValue }) => (
            <form style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <FieldArray name="invoice_products" render={(arrayHelpers) => (
                <>
                  {/* ── Scrollable item list ── */}
                  <Box sx={{
                    flex: 1, overflowY: "auto",
                    px: "1.5rem", py: "0.75rem",
                    display: "flex", flexDirection: "column", gap: "8px",
                    "&::-webkit-scrollbar": { width: "3px" },
                    "&::-webkit-scrollbar-thumb": {
                      background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                      borderRadius: "3px",
                    },
                  }}>
                    {values.invoice_products.length === 0 ? (
                      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: "4rem", gap: "8px" }}>
                        <Box sx={{ width: "44px", height: "44px", borderRadius: "50%", backgroundColor: "rgba(29,158,117,0.08)", display: "flex", alignItems: "center", justifyContent: "center", color: accent }}>
                          <ReceiptIcon sx={{ fontSize: "20px" }} />
                        </Box>
                        <Typography sx={{ fontSize: "13px", color: textSec }}>
                          No items — search a product below to add it
                        </Typography>
                      </Box>
                    ) : (
                      values.invoice_products.map((row, index) => (
                        <InvoiceDetailForm
                          key={row.data_id ?? row.id ?? index}
                          index={index}
                          data={invoiceDetailFormData[index] ?? row}
                          handleRemoveProductDetailRow={(i) => {
                            arrayHelpers.remove(i);
                            handleRemoveProductDetailRow(i);
                          }}
                          handleItemAmountChange={handleItemAmountChange}
                          handleItemDiscountChange={handleItemDiscountChange}
                          handleChange={handleChange}
                          errors={arrayHelpers.form.errors}
                          touched={arrayHelpers.form.touched}
                          name={`invoice_products[${index}]`}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                        />
                      ))
                    )}
                  </Box>

                  {/* ══ FIXED FOOTER ══ */}
                  <Box sx={{ flexShrink: 0, backgroundColor: cardBg, borderTop: `0.5px solid ${border}` }}>

                    {/* Search bar */}
                    <Box sx={{ px: "1.5rem", pt: "10px", pb: "6px", position: "relative" }}>
                      <Box sx={{
                        display: "flex", alignItems: "center", gap: "8px",
                        px: "12px", py: "8px",
                        border: `1.5px solid ${showResults || searchQuery.length > 0 ? accent : border}`,
                        borderRadius: "10px", backgroundColor: bodyBg, transition: "border-color 0.15s",
                      }}>
                        <SearchIcon sx={{ fontSize: "16px", color: accent, flexShrink: 0 }} />
                        <InputBase
                          placeholder="Search product to add (min 2 chars)…"
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onFocus={() => { if (searchResults.length > 0) setShowResults(true); }}
                          onBlur={() => setTimeout(() => setShowResults(false), 150)}
                          sx={{ flex: 1, fontSize: "13px", color: textPri, "& input::placeholder": { color: textSec } }}
                        />
                      </Box>

                      {/* Dropdown — opens upward */}
                      {showResults && searchResults.length > 0 && (
                        <Box sx={{
                          position: "absolute", bottom: "100%", left: "1.5rem", right: "1.5rem",
                          backgroundColor: cardBg, border: `0.5px solid ${border}`,
                          borderRadius: "10px", mb: "4px",
                          maxHeight: "220px", overflowY: "auto", zIndex: 100,
                          boxShadow: isDark ? "0 -8px 24px rgba(0,0,0,0.4)" : "0 -8px 24px rgba(0,0,0,0.1)",
                          "&::-webkit-scrollbar": { width: "3px" },
                          "&::-webkit-scrollbar-thumb": { background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", borderRadius: "3px" },
                        }}>
                          {searchResults.map((product, i) => (
                            <Box
                              key={product.id}
                              onMouseDown={() => {
                                const newRow = normaliseProduct({
                                  related_product:      product.id,
                                  related_product_unit: "",
                                  batch:                "",
                                  quantity:             0,
                                  sale_price:           "",
                                  discount:             0,
                                  total:                0,
                                  product_name:         `${product.product_name} ${product.description}`,
                                  id:                   uuidv4(),
                                });
                                arrayHelpers.push(newRow);
                                setInvoiceDetailFormData((prev) => [...prev, newRow]);
                                setSearchQuery("");
                                setShowResults(false);
                              }}
                              sx={{
                                display: "flex", alignItems: "center", gap: "10px",
                                px: "12px", py: "10px",
                                borderBottom: i < searchResults.length - 1 ? `0.5px solid ${border}` : "none",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: isDark ? "rgba(29,158,117,0.07)" : "rgba(29,158,117,0.04)" },
                                transition: "background 0.1s",
                              }}
                            >
                              <Box sx={{ width: "22px", height: "22px", borderRadius: "50%", border: `1.5px solid ${accent}`, display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
                                <AddCircleOutlineIcon sx={{ fontSize: "14px" }} />
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                                  {product.product_name} {product.description}
                                </Typography>
                                <Typography sx={{ fontSize: "11px", color: textSec, mt: "1px" }}>
                                  {product.unit}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Totals + actions */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", px: "1.5rem", pt: "6px", pb: "12px", gap: "12px" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                        <Box>
                          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
                            {values.invoice_products.length} items · discount
                          </Typography>
                          <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#e24b4a" }}>
                            -{Number(totalDiscount).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ width: "1px", height: "28px", backgroundColor: border }} />
                        <Box>
                          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
                            Grand total
                          </Typography>
                          <Typography sx={{ fontSize: "20px", fontWeight: 500, color: accent }}>
                            {Number(grandTotal).toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Button
                          onClick={() => { resetForm(); navigate("/billing"); }}
                          startIcon={<CancelIcon sx={{ fontSize: "15px" }} />}
                          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", textTransform: "none", backgroundColor: "#e24b4a", color: "#fff", "&:hover": { backgroundColor: "#c23a39" } }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => validateForm().then(() => { if (isValid) setOpen(true); })}
                          startIcon={<CheckCircleIcon sx={{ fontSize: "15px" }} />}
                          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", textTransform: "none", backgroundColor: accent, color: "#fff", "&:hover": { backgroundColor: "#0F6E56" } }}
                        >
                          {!isValid ? "Verify Bill" : "Update Bill"}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </>
              )} />
            </form>
          )}
        </Formik>
      )}

      {/* ══ Dialogs ══ */}
      <FixedSizeDialog
        open={open}
        handleClose={() => setOpen(false)}
        dialogHeading="Confirm Bill Changes"
        positiveResponse="Confirm Update"
        handleSubmit={saveInvoice}
        isSubmitting={isSubmittingInvoice}
        isSubmittingText="Updating bill, please wait…"
        negativeResponse="Cancel"
        content={
          <InvoiceConfirmView
            invoiceDescription="Edit Bill"
            invoiceDetails={invoiceDetailFormData}
            total={grandTotal}
            discount={totalDiscount}
          />
        }
      />

      <FixedSizeDialog
        open={openNewCustomer}
        handleClose={() => setOpenNewCustomer(false)}
        dialogHeading="Add New Customer"
        positiveResponse="Create Customer"
        handleSubmit={saveNewCustomer}
        isSubmitting={isSubmittingCustomer}
        isSubmittingText="Creating customer…"
        negativeResponse="Cancel"
        content={
          <CustomerForm
            handleChangeFirstName={(e) => setfirstName(e.target.value)}
            handleChangeLastName={(e)  => setLastName(e.target.value)}
            handleChangeEmailAddress={(e) => setEmail(e.target.value)}
            handleChangeAddress={(e)   => setAddress(e.target.value)}
            handleChangePhoneNumber={(e) => setPhoneNumber(e.target.value)}
          />
        }
      />

      <TopSnack
        vertical={openSnack.vertical}
        horizontal={openSnack.horizontal}
        open={openSnack.open}
        handleCloseSnack={handleCloseSnack}
        message={openSnack.message}
        severity={openSnack.severity}
      />
    </Box>
  );
};

export default InvoiceEditForm;